import { Controller } from "@hotwired/stimulus";
import { rrFetch } from "../utils/rr_fetch";
import {
  digitsOf, expiryStatus, formatCardNumber, formatExpiry, isCardNumberComplete, luhnValid,
} from "./cardFormHelpers.mjs";

// The card check is advisory: past this, Save stops waiting for its answer.
const CHECK_TIMEOUT_MS = 10000;

export default class CardPreviewController extends Controller {
  static targets = [
    "cardNumberInput", "nameInput", "expInput",
    "cardNumberPreview", "namePreview", "expPreview",
    "cardNumberError", "expError", "saveButton",
  ];

  static values = {
    checkUrl: String,
    csrfToken: String,
    invalidNumberMessage: String,
    invalidExpiryMessage: String,
    expiredMessage: String,
  };

  connect() {
    // A restored page keeps what was typed, so the fields are the source of truth.
    this.cardDigits = digitsOf(this.cardNumberInputTarget.value);
    this.expDigits = digitsOf(this.expInputTarget.value);
    this.checkedDigits = "";
    this.checkState = "idle";
    this.checkCardNumber(this.cardDigits);
    this.showExpiryError(this.expDigits);
    this.updateSaveButton();
  }

  disconnect() {
    this.abortCheck();
  }

  onCardNumberInput(event) {
    const input = this.cardNumberInputTarget;
    const result = formatCardNumber(input.value, caretOf(input), event?.inputType, this.cardDigits);
    this.cardDigits = result.digits;
    applyFormatted(input, result);

    if (this.hasCardNumberPreviewTarget) {
      // Preview shows only the first 4 typed digits; remaining groups stay masked
      // to match the Add Card Figma spec ("4821 •••• •••• ••••").
      const first4 = result.digits.slice(0, 4).padEnd(4, "•");
      this.cardNumberPreviewTarget.textContent = `${first4} •••• •••• ••••`;
    }

    this.checkCardNumber(result.digits);
    this.updateSaveButton();
  }

  onNameInput() {
    if (this.hasNamePreviewTarget) {
      const name = (this.nameInputTarget.value || "").trim().toUpperCase();
      this.namePreviewTarget.textContent = name || "FULL NAME";
    }
    this.updateSaveButton();
  }

  onExpInput(event) {
    const input = this.expInputTarget;
    const result = formatExpiry(input.value, caretOf(input), event?.inputType, this.expDigits);
    this.expDigits = result.digits;
    applyFormatted(input, result);

    if (this.hasExpPreviewTarget) {
      const mm = result.digits.slice(0, 2).padEnd(2, "M");
      const yy = result.digits.slice(2, 4).padEnd(2, "Y");
      this.expPreviewTarget.textContent = `${mm}/${yy}`;
    }

    this.showExpiryError(result.digits);
    this.updateSaveButton();
  }

  // guardSubmit backs up the disabled button: the form never leaves the page
  // until every check has passed.
  guardSubmit(event) {
    if (!this.isFormValid()) event.preventDefault();
  }

  isFormValid() {
    return this.isCardNumberValid()
      && this.nameInputTarget.value.trim() !== ""
      && expiryStatus(this.expDigits) === "valid";
  }

  // A complete number is held back only while the check is running or after it
  // refused the card. A check that could not answer leaves the decision to Save.
  isCardNumberValid() {
    return isCardNumberComplete(this.cardDigits)
      && this.checkState !== "pending"
      && this.checkState !== "rejected";
  }

  updateSaveButton() {
    if (!this.hasSaveButtonTarget) return;
    this.saveButtonTarget.disabled = !this.isFormValid();
  }

  // checkCardNumber runs the card check once the number is complete, so a card
  // we refuse is flagged before the rest of the form is filled. Save checks again.
  checkCardNumber(digits) {
    if (digits === this.checkedDigits) return;
    this.abortCheck();
    this.checkedDigits = digits;
    this.checkState = "idle";
    this.showCardNumberError("");
    if (!isCardNumberComplete(digits)) return;

    if (!luhnValid(digits)) {
      this.settleCheck("rejected", this.invalidNumberMessageValue);
      return;
    }
    if (this.hasCheckUrlValue) this.runCheck(digits);
  }

  runCheck(digits) {
    this.checkState = "pending";
    const abort = new AbortController();
    this.checkAbort = abort;
    this.checkTimer = setTimeout(() => this.settleCheck("unavailable"), CHECK_TIMEOUT_MS);

    rrFetch(this.checkUrlValue, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new URLSearchParams({ card_number: digits, csrf_token: this.csrfTokenValue }),
      signal: abort.signal,
    })
      .then((response) => {
        const isJSON = (response.headers.get("Content-Type") || "").includes("application/json");
        return response.ok && isJSON ? response.json() : null;
      })
      .then((result) => {
        if (abort.signal.aborted) return;
        if (result?.rejected) {
          this.settleCheck("rejected", result.message);
        } else {
          this.settleCheck(result ? "passed" : "unavailable");
        }
      })
      .catch(() => {
        // Advisory only: when the check cannot answer, Save decides.
        if (!abort.signal.aborted) this.settleCheck("unavailable");
      });
  }

  // settleCheck records the verdict and stops whatever is still waiting on it.
  settleCheck(state, message = "") {
    this.abortCheck();
    this.checkState = state;
    if (message) this.showCardNumberError(message);
    this.updateSaveButton();
  }

  abortCheck() {
    clearTimeout(this.checkTimer);
    this.checkAbort?.abort();
    this.checkAbort = null;
  }

  showCardNumberError(message) {
    showFieldError(this.cardNumberInputTarget, this.hasCardNumberErrorTarget ? this.cardNumberErrorTarget : null, message);
  }

  showExpiryError(digits) {
    const status = expiryStatus(digits);
    let message = "";
    if (status === "invalid") message = this.invalidExpiryMessageValue;
    if (status === "expired") message = this.expiredMessageValue;
    showFieldError(this.expInputTarget, this.hasExpErrorTarget ? this.expErrorTarget : null, message);
  }
}

function caretOf(input) {
  return input.selectionStart ?? input.value.length;
}

function applyFormatted(input, { value, caret }) {
  if (input.value === value) return;
  input.value = value;
  if (document.activeElement === input) input.setSelectionRange(caret, caret);
}

function showFieldError(input, errorEl, message) {
  input.setAttribute("aria-invalid", message ? "true" : "false");
  if (!errorEl) return;
  errorEl.textContent = message;
  errorEl.classList.toggle("hidden", !message);
}
